export const problemSection = {
  title: '¿Te resulta difícil encontrar ejercicios y progresiones para cada necesidad clínica?',
  intro: 'Cada paciente presenta diferentes limitaciones y objetivos de rehabilitación. En tu práctica necesitás decidir:',
  list: [
    'Qué ejercicio seleccionar según la evaluación.',
    'Cómo ejecutarlo correctamente.',
    'Qué series, repeticiones e intensidad indicar.',
    'Cuándo aumentar o reducir la dificultad.',
    'Cómo adaptar el programa según la evolución del paciente.',
  ],
  outro: 'ATLAS MIOFASCIAL™ reúne estos recursos en una biblioteca visual organizada para facilitar tu trabajo clínico.',
  highlight: 'Evaluar. Seleccionar. Aplicar. Progresar.',
};

export const volumes = [
  {
    number: '01',
    title: 'Fascia plantar, pie y dedos',
    text: 'Ejercicios y protocolos para movilidad, fortalecimiento, tolerancia al apoyo y recuperación funcional del pie.',
    includes: [
      'Movilidad de fascia plantar y dedos',
      'Fortalecimiento intrínseco del pie',
      'Progresión de tolerancia al apoyo y la carga',
      'Criterios de descarte y derivación',
    ],
  },
  {
    number: '02',
    title: 'Tobillo, pantorrilla y cadena inferior',
    text: 'Ejercicios para gastrocnemio, sóleo y tobillo, con progresiones de fuerza, movilidad y resistencia muscular.',
    includes: [
      'Estiramientos de gastrocnemio y sóleo',
      'Movilidad y dorsiflexión de tobillo',
      'Fuerza con banda elástica y elevación de talones',
      'Errores frecuentes y cómo corregirlos',
    ],
  },
  {
    number: '03',
    title: 'Cadera, glúteos y banda iliotibial',
    text: 'Recursos para fuerza, control funcional, capacidad muscular y progresión de carga de cadera y glúteos.',
    includes: [
      'Activación y fuerza de glúteo medio y mayor',
      'Trabajo de banda iliotibial y tensor de la fascia lata',
      'Control de pelvis y estabilidad funcional',
      'Progresión de carga de menor a mayor demanda',
    ],
  },
  {
    number: '04',
    title: 'Cervical, cuello, trapecio y hombro',
    text: 'Ejercicios de movilidad cervical, fortalecimiento, resistencia y control de la región escapular.',
    includes: [
      'Movilidad cervical activa y controlada',
      'Descarga y fortalecimiento de trapecio',
      'Control escapular y estabilidad de hombro',
      'Resistencia postural para el día a día',
    ],
  },
  {
    number: '05',
    title: 'Espalda, zona torácica y lumbar miofascial',
    text: 'Protocolos de movilidad, fortalecimiento, resistencia y recuperación de actividades funcionales de la espalda.',
    includes: [
      'Movilidad torácica y rotación de tronco',
      'Fortalecimiento de la musculatura lumbar y paravertebral',
      'Control del core y estabilidad',
      'Retorno a actividades funcionales',
    ],
  },
  {
    number: '06',
    title: 'Cara, mandíbula y región miofascial facial',
    text: 'Ejercicios terapéuticos de movilidad mandibular, coordinación y recuperación funcional orofacial para pacientes evaluados.',
    includes: [
      'Movilidad y apertura mandibular controlada',
      'Coordinación de la articulación temporomandibular',
      'Trabajo de masetero y musculatura facial',
      'Recuperación funcional orofacial',
    ],
  },
];

// Páginas del carrusel "Echá un vistazo al interior", en orden 1–6.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/f4FRC59F1cEJjoHv2NDiUn/Captura_de_pantalla_2026-11-02_150450.png',
    alt: 'Cuándo no aplicar una rutina de fascia plantar — tabla de descarte y derivación con signos compatibles e incompatibles.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/6DPPpcjykLL3ozLykq6NKH/Captura_de_pantalla_2026-11-02_150524.png',
    alt: 'Ejercicios AM01-E001 a E003 — alta irritabilidad y baja demanda, con fotos paso a paso y dosificación.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/8wLE1WSXfyJZLVQtbxqZ9H/Captura_de_pantalla_2026-11-02_150546.png',
    alt: 'Ejercicios AM01-E004 a E006 — protocolo de movilidad de tobillo con errores frecuentes y progresiones.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/5Cj9uyUGwEkoN5NefhWA9E/Captura_de_pantalla_2026-11-02_150618.png',
    alt: 'Ejercicios AM01-E016 a E018 — respuesta insuficiente, problema clínico que resuelve y alternativas por intolerancia.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/ciVk98rRiWBLgdKMDFkBhQ/Captura_de_pantalla_2026-11-02_150631.png',
    alt: 'Ejercicios AM01-E019 a E021 — protocolo sin compresión directa del nódulo, con regresiones y progresiones.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/fSBNoNRGkXmtYHGNbxj8tX/Captura_de_pantalla_2026-11-02_150659.png',
    alt: 'Protocolo 10 — fasciopatía plantar en adolescentes, con criterios diferenciales frente a apofisitis calcánea.',
  },
];

export const insideFeatures = [
  'Imágenes paso a paso.',
  'Posición inicial y ejecución.',
  'Series, repeticiones y duración.',
  'Intensidad y descansos.',
  'Progresiones y regresiones.',
  'Adaptaciones según la respuesta.',
  'Precauciones y seguimiento funcional.',
];

export const bonuses = [
  {
    number: '01',
    title: 'Protocolos visuales por diagnóstico',
    highlight: '30 rutas clínicas de decisión.',
    text: 'Herramientas para seleccionar ejercicios, identificar precauciones y organizar intervenciones según la evaluación.',
    includes: ['Qué ejercicio elegir según el hallazgo clínico', 'Señales de alerta y cuándo derivar', 'Orden sugerido de la intervención'],
  },
  {
    number: '02',
    title: 'Biblioteca de progresiones y regresiones',
    highlight: '30 mapas visuales de adaptación.',
    text: 'Alternativas para modificar amplitud, resistencia, apoyo, duración y dificultad de los ejercicios.',
    includes: ['Versión más fácil y más difícil de cada ejercicio', 'Cuándo progresar y cuándo retroceder', 'Opciones si el paciente no tolera la carga'],
  },
  {
    number: '03',
    title: 'Sesiones visuales listas para aplicar',
    highlight: '30 sesiones terapéuticas organizadas.',
    text: 'Ejercicios estructurados con tiempos, dosificaciones, descansos y seguimiento clínico.',
    includes: ['Sesiones armadas de principio a fin', 'Tiempos y descansos ya calculados', 'Registro de evolución de cada sesión'],
  },
  {
    number: '04',
    title: 'Programas domiciliarios visuales',
    highlight: '30 programas para personalizar y entregar.',
    text: 'Hojas ilustradas con ejercicios, indicaciones, frecuencia semanal y seguimiento del paciente.',
    includes: ['Hojas listas para imprimir o enviar', 'Indicaciones claras para el paciente', 'Planilla de seguimiento semanal'],
  },
  {
    number: '05',
    title: 'Errores comunes y correcciones de movimiento',
    highlight: '30 fichas visuales de corrección.',
    text: 'Comparaciones de ejecución, ajustes de movimiento y herramientas para reevaluar la técnica.',
    includes: ['Ejecución correcta vs. incorrecta', 'Indicaciones para corregir en el momento', 'Criterios para reevaluar la técnica'],
  },
];

export const benefits = [
  { title: 'Ejercicios prácticos', text: 'Secuencias ilustradas con instrucciones de ejecución.' },
  { title: 'Protocolos organizados', text: 'Recursos para diferentes presentaciones y objetivos clínicos.' },
  { title: 'Dosificación y adaptaciones', text: 'Series, repeticiones, descansos, progresiones y regresiones.' },
  { title: 'Cobertura de seis regiones', text: 'Desde la fascia plantar hasta la región orofacial.' },
  { title: 'Material imprimible', text: 'Hojas de consulta, sesiones y programas domiciliarios personalizables.' },
  { title: 'Todo en una colección', text: 'Once recursos digitales para complementar tu práctica profesional.' },
];

export const offerItems = [
  '6 volúmenes de ejercicios y protocolos.',
  '5 bonos profesionales GRATIS.',
  'Ejercicios ilustrados paso a paso.',
  'Dosificaciones, progresiones y regresiones.',
  'Sesiones y programas domiciliarios.',
  '440 páginas de recursos clínicos.',
];

export const audiences = [
  { title: 'Kinesiólogos', text: 'Ejercicios y protocolos para rehabilitación musculoesquelética.' },
  { title: 'Fisioterapeutas', text: 'Recursos visuales para complementar intervenciones clínicas.' },
  { title: 'Especialistas en ejercicio terapéutico', text: 'Dosificaciones, adaptaciones y progresiones funcionales.' },
  {
    title: 'Profesionales de rehabilitación deportiva',
    text: 'Herramientas para desarrollar fuerza, resistencia y retorno funcional.',
  },
];

export const testimonials = [
  {
    name: 'Valentina Gómez',
    age: 34,
    role: 'Kinesióloga · Córdoba, Argentina',
    quote:
      'Atiendo muchas fascitis plantares en corredores. Las progresiones y regresiones del volumen 01 me ahorran tiempo antes de cada sesión, y los programas domiciliarios los entrego impresos: los pacientes por fin entienden qué hacer en casa.',
  },
  {
    name: 'Martín Ríos',
    age: 42,
    role: 'Kinesiólogo · Mendoza, Argentina',
    quote:
      'En consultorio veo mucha cervicalgia y sobrecarga de trapecio en trabajadores de oficina. Tener la dosificación y las adaptaciones según la respuesta en una sola ficha me ordenó el trabajo: abro el volumen 04, selecciono y adapto.',
  },
  {
    name: 'Carolina Méndez',
    age: 38,
    role: 'Kinesióloga deportiva · Mar del Plata, Argentina',
    quote:
      'Trabajo con un equipo de handball y las sesiones listas del bono 03 me resolvieron la planificación de cadera y pantorrilla. Lo que más uso son las fichas de errores comunes para corregir la técnica en el momento.',
  },
];

export const faqs = [
  { q: '¿Es un producto físico?', a: 'No. Es una colección 100% digital. No se envían libros físicos.' },
  { q: '¿Está completamente en español?', a: 'Sí. Todo el material está desarrollado en español.' },
  {
    q: '¿Para quién está pensado?',
    a: 'Para kinesiólogos, fisioterapeutas, especialistas en ejercicio terapéutico y profesionales de rehabilitación deportiva.',
  },
  {
    q: '¿Incluye ejercicios con imágenes?',
    a: 'Sí. Contiene fichas visuales con instrucciones, dosificaciones y adaptaciones.',
  },
  { q: '¿Los cinco bonos son gratuitos?', a: 'Sí. Están incluidos en el precio de la colección.' },
  {
    q: '¿Puedo imprimir los materiales?',
    a: 'Sí, para utilizarlos dentro de tu práctica profesional y conforme a los términos de uso.',
  },
  {
    q: '¿Los protocolos sirven para cualquier paciente?',
    a: 'No. Deben seleccionarse y adaptarse según la evaluación clínica, las restricciones y las necesidades individuales.',
  },
  {
    q: '¿Reemplaza la evaluación clínica?',
    a: 'No. Es un recurso educativo profesional que complementa tu criterio clínico; no sustituye la evaluación individualizada ni el diagnóstico médico.',
  },
  {
    q: '¿Cómo recibo mi compra?',
    a: 'El acceso al material digital se entrega automáticamente después de confirmarse el pago.',
  },
  { q: '¿Cómo contacto con soporte?', a: 'Escribinos a compradigitalyaa@gmail.com.' },
];

export const recentBuyers = [
  { name: 'Lucía Fernández', place: 'Rosario, Santa Fe', minutes: 3 },
  { name: 'Facundo Sosa', place: 'San Miguel de Tucumán, Tucumán', minutes: 7 },
  { name: 'Agustina Pereyra', place: 'La Plata, Buenos Aires', minutes: 5 },
  { name: 'Matías Acosta', place: 'Neuquén, Neuquén', minutes: 12 },
  { name: 'Florencia Díaz', place: 'Salta, Salta', minutes: 4 },
  { name: 'Nicolás Benítez', place: 'Corrientes, Corrientes', minutes: 9 },
  { name: 'Micaela Romero', place: 'Bahía Blanca, Buenos Aires', minutes: 14 },
  { name: 'Joaquín Villalba', place: 'Posadas, Misiones', minutes: 6 },
  { name: 'Rocío Ledesma', place: 'Comodoro Rivadavia, Chubut', minutes: 11 },
  { name: 'Santiago Quiroga', place: 'San Juan, San Juan', minutes: 8 },
];
